import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { pollingInterval } from "../../../constants/pollingInterval";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { AuthContext } from "../../../context/AuthContext";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface IAccountLatency {
  account: string;
  latency?: number;
  latency_ms?: number;
  avg_latency?: number;
  p50?: number;
  p95?: number;
  maker_latency_ms?: number;
  taker_latency_ms?: number;
  [key: string]: unknown;
}

export const useAccountLatency = (account?: string) => {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const { addToast } = useToast();
  const endpoint = ADMIN_API_URL(AdminEndpointEnum.ACCOUNT_LATENCY);

  const swr = useSWR<IAccountLatency, AxiosError>(
    tokenId && account ? [endpoint, tokenId, account] : null,
    async () => {
      const accountLatency = await axios.get<IAccountLatency>(endpoint, {
        ...queryConfig(),
        params: {
          account,
        },
      });

      return accountLatency.data;
    },
    {
      refreshInterval: pollingInterval[AdminEndpointEnum.ACCOUNT_LATENCY],
    }
  );

  const updateAccountLatency = useCallback(
    async (makerLatencyMs?: number, takerLatencyMs?: number) => {
      if (!account) return;

      try {
        const payload = {
          account,
          maker_latency_ms: makerLatencyMs ?? swr.data?.maker_latency_ms ?? 0,
          taker_latency_ms: takerLatencyMs ?? swr.data?.taker_latency_ms ?? 0,
        };

        await axios.post(endpoint, payload, {
          headers: {
            Authorization: `Bearer ${tokenId}`,
          },
        });

        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Account Latency Updated",
            subheader: "Account latency has been successfully updated",
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );

        swr.mutate();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);

        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Account Latency Update Failed",
            subheader: ((error as AxiosError)?.response?.data as any)?.error || "Unknown error",
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
      }
    },
    [account, addToast, endpoint, swr, swr.data, tokenId]
  );

  return {
    ...swr,
    updateAccountLatency,
  };
};
